import {
  AI_BG_NEW,
  AI_Platform_Hero_New,
  APU_Icon,
  Connect_Icon,
  Context_Aware2_Icon,
  Our_Product_Bg,
  Why_AI_Platform,
  Your_Problem_Img,
} from "../../assets";
import { Helmet, HelmetData } from "react-helmet-async";
import { useModal } from "../../utils/demoProvider";
import { CheckMarkCircleIcon } from "../../icons/checkMarkCircleIcon";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";

const why = [
  {
    icon: Connect_Icon,
    title: "Connect your data",
    text: "Bring your existing data lakes, warehouses, and streaming feeds. [AI] Platform's ingestion layer handles batch and real-time sources.",
  },
  {
    icon: APU_Icon,
    title: "Run your workloads ",
    text: "Submit analytics jobs to APU clusters and AI workloads to GPU clusters, no code changes required. The hardware handles optimisation and processing at silicon speed.",
  },
  {
    icon: Context_Aware2_Icon,
    title: "Scale on demand",
    text: "Grow from a single job to production pipelines on dedicated or shared clusters. No capacity planning. No over-provisioning. We manage the rest.",
  },
];

export default function AiPlatformNew() {
  const helmetData = new HelmetData({});
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        // Hero
        maskedLineReveal(pageRef.current.querySelector(".aip-hero-heading"), {
          scrollTrigger: revealTrigger(".aip-hero-heading"),
        });
        riseFrom(".aip-hero-copy", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".aip-hero-copy", "top 85%"),
        });
        riseFrom(".aip-hero-cta", {
          delay: 0.35,
          scrollTrigger: revealTrigger(".aip-hero-cta", "top 90%"),
        });
        riseFrom(".aip-hero-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".aip-hero-media"),
        });

        // The problem / the solution
        riseFrom(".aip-problem-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".aip-problem-head"),
        });
        riseFrom(".aip-solution-img", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".aip-solution-img"),
        });
        riseFrom(".aip-solution-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".aip-solution-head"),
        });
        riseFrom(".aip-pill", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".aip-pills", "top 75%"),
        });

        // How it works
        riseFrom(".aip-how-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".aip-how-head"),
        });
        riseFrom(".aip-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".aip-grid", "top 75%"),
        });

        // GPU clusters
        riseFrom(".aip-gpu-img", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".aip-gpu-img"),
        });
        riseFrom(".aip-gpu-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".aip-gpu-head"),
        });

        // Closing CTA card
        riseFrom(".aip-cta > *", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".aip-cta"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef },
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          [AI] Platform — Managed Compute for Analytics &amp; AI Workloads
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/ai-platform"
        />
        <meta
          name="description"
          content="[AI] Platform is a managed compute service by Analytics Intelligence — run analytics and AI workloads on Speedata APUs and NVIDIA GPUs, up to 100× faster and at lower cost, in the cloud or on-premise."
        />
        <meta
          name="keywords"
          content="[AI] Platform, managed compute service, Analytics Processing Unit, APU compute, NVIDIA GPU compute, Apache Spark acceleration, Speedata APU, on-premise AI compute, cloud analytics infrastructure, GPU clusters for AI, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="[AI] Platform — Managed Compute for Analytics & AI Workloads"
        />
        <meta
          property="og:description"
          content="Managed compute on Speedata APUs and NVIDIA GPUs — run analytics and AI workloads up to 100× faster with zero code changes, in the cloud or on-premise."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/ai-platform"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="[AI] Platform — Managed Compute for Analytics & AI Workloads"
        />
        <meta
          name="twitter:description"
          content="Managed compute on Speedata APUs and NVIDIA GPUs — run analytics and AI workloads up to 100× faster with zero code changes, in the cloud or on-premise."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "[AI] Platform — Managed Compute Service",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, Cloud, On-Premise",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "[AI] Platform is a managed compute service by Analytics Intelligence for analytics and AI workloads, powered by Speedata Analytics Processing Units (APUs) and NVIDIA GPUs. It accelerates Apache Spark up to 100× at lower cost with no code changes, deployable in the cloud or on-premise.",
            "url": "https://analyticsintelligence.com/ai-platform",
            "featureList": ["Managed APU compute", "NVIDIA GPU clusters", "Apache Spark acceleration", "Zero code changes", "Cloud or on-premise deployment", "Instant scaling"]
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-20 md:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[28px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  [AI] Platform
                </span>
                <h2
                  className="aip-hero-heading text-[38px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.2] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      'Managed compute. Purpose-built silicon. <br class="hidden lg:block" /> Zero infrastructure headaches.',
                  }}
                />
                <p className="aip-hero-copy text-sm md:text-lg text-center md:text-left font-medium text-[#FFFFFF] leading-5">
                  [AI] Platform is a managed compute service for analytics and
                  AI workloads — powered by Speedata's Analytics Processing
                  Units (APUs) and NVIDIA GPUs, in the cloud or on-premise.
                  Faster workloads, lower costs, nothing to manage.
                </p>
              </div>

              <div className="aip-hero-cta flex flex-row justify-center md:justify-start gap-3 sm:gap-4 w-full">
                <button
                  onClick={openModal}
                  className="text-sm md:text-base font-semibold py-3 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
                >
                  Get Started
                </button>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className="aip-hero-media w-full"
                src={AI_Platform_Hero_New}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pb-[96px]">
          <div className="container relative flex flex-col gap-11 isolate">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

            <div className="aip-problem-head flex flex-col items-center text-center md:text-left z-[1000]">
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
                The problem
              </h2>

              <h3 className="text-base md:text-xl font-extrabold text-[#88BEFF] my-1 text-center">
                Your compute wasn't built for your data
              </h3>

              <p className="text-white text-sm md:text-base text-center max-w-[837px]">
                General-purpose CPUs choke on modern analytics workloads; memory
                overhead, serialisation, and slow data shuffles force you to
                over-provision, overspend, and still wait.
              </p>
            </div>
            <div className="flex flex-col-reverse lg:flex-row items-center mt-0 md:mt-11 gap-16 lg:gap-8">
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={Your_Problem_Img}
                  alt="Data Structures"
                  className="aip-solution-img w-full max-w-[546px] h-auto"
                />
              </div>

              <div class="flex-1 space-y-8">
                <div className="aip-solution-head">
                  <h2 class="text-xl md:text-2xl font-extrabold text-white text-center lg:text-left">
                    The solution
                  </h2>

                  <h3 className="text-base md:text-xl font-extrabold text-[#88BEFF] my-1 text-center lg:text-left">
                    Managed APU: analytics at silicon speed
                  </h3>
                  <p className="text-white text-sm md:text-base max-w-[1086px] text-center lg:text-left">
                    At the core of [AI] Platform is the APU, a purpose-built
                    chip from our silicon partner Speedata that executes
                    queries, shuffles, and optimisation directly in hardware.
                    Every APU advance flows straight to your workloads, with
                    nothing to migrate. Apache Spark is one example of what it
                    unlocks:
                  </p>
                </div>

                <div class="aip-pills space-y-6 mb-12">
                  <div className="aip-pill bg-[#EFF0F71F] flex py-2 md:py-4 px-3 md:px-6 rounded-[100px] gap-2">
                    <CheckMarkCircleIcon />
                    <p className="text-white text-sm md:text-base">
                      <span className="font-extrabold text-[#88BEFF]">
                        Up to 100× faster:{" "}
                      </span>
                      Spark queries on APU vs. standard cloud compute — and
                      Spark is just the start
                    </p>
                  </div>
                  <div className="aip-pill bg-[#EFF0F71F] flex py-2 md:py-4 px-3 md:px-6 rounded-[100px] gap-2">
                    <CheckMarkCircleIcon />
                    <p className="text-white text-sm md:text-base">
                      <span className="font-extrabold text-[#88BEFF]">
                        Up to 20% lower cost:{" "}
                      </span>
                      Per query through hardware efficiency
                    </p>
                  </div>
                  <div className="aip-pill bg-[#EFF0F71F] flex py-2 md:py-4 px-3 md:px-6 rounded-[100px] gap-2">
                    <CheckMarkCircleIcon />
                    <p className="text-white text-sm md:text-base">
                      <span className="font-extrabold text-[#88BEFF]">
                        Zero code changes:{" "}
                      </span>
                      Drop-in compatible with your existing data workloads
                    </p>
                  </div>
                  <div className="aip-pill bg-[#EFF0F71F] flex py-2 md:py-4 px-3 md:px-6 rounded-[100px] gap-2">
                    <CheckMarkCircleIcon />
                    <p className="text-white text-sm md:text-base">
                      <span className="font-extrabold text-[#88BEFF]">
                        Instant scale:{" "}
                      </span>
                      Spin up APU or GPU clusters on demand, pay only for what
                      you use
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="aip-how-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                How It Works
              </h2>
              <p className="text-[#0073FF] text-lg md:text-2xl text-center font-bold">
                One managed service. The right silicon for every workload.
              </p>
            </div>

            <div className="aip-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="aip-card group col-span-12 md:col-span-6 lg:col-span-4 bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pb-[48px] pt-[96px] md:py-[96px]">
          <div className="container relative flex flex-col">
            <div className="w-[150px] lg:w-[400px] h-[400px] bg-[#0073FF]/40 blur-[120px] absolute top-[50%] lg:top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
            <div className="flex flex-col-reverse md:flex-row-reverse items-center mt-11 gap-16 md:gap-8 z-[100]">
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={Why_AI_Platform}
                  alt="Data Structures"
                  className="aip-gpu-img w-full max-w-[400px] h-auto"
                />
              </div>

              <div class="aip-gpu-head flex-1 space-y-4">
                <h2 class="text-2xl text-center md:text-left md:text-[32px] font-extrabold text-white">
                  Managed APU
                </h2>

                <div className="space-y-2 text-center md:text-left">
                  <h2 className="text-lg md:text-2xl font-bold text-[#88BEFF]">
                    First-of-its-kind silicon, yours as a service
                  </h2>
                  <p className="text-sm md:text-base text-white">
                    Speedata built the APU for big data. [AI] Platform makes it
                    a service: APU clusters on demand, zero code changes, pay
                    only for what you use. As the silicon advances, your
                    workloads get faster, nothing to buy, rack, or manage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="relative pt-[116px] pb-10"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="aip-cta bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="">
                <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white">
                  Prove it on your own workload
                </h2>
                <p className="text-sm md:text-base text-center text-[#FFFFFF] max-w-[680px]">
                  Don't take our word for it. Run your existing workload on [AI]
                  Platform, benchmark it against your current infrastructure,
                  and see the numbers yourself. Most teams see results in under
                  an hour.
                </p>
              </div>

              <Link to="/contact">
                <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[120px] sm:min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                  Get Started
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
